import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSchool } from '../context/SchoolContext';
import { useMobileMenu } from '../hooks/useMobileMenu';
import { LogoutConfirmationModal } from '../components/LogoutConfirmationModal';
import { 
  uploadAvatar, 
  updateUserProfile, 
  type AssignmentResource, 
  fetchAssignments, 
  type AssignmentDto, 
  fetchAnnouncements, 
  type AnnouncementDto,
  fetchTimetables,
  type TimetableDto,
  fetchClubs,
  type ClubDto,
  fetchActiveClasses,
  type SchoolClassDto,
  fetchStudentTermProfile,
  saveStudentTermProfile,
  fetchClassAnnouncementsForStudent,
  type ClassAnnouncementDto,
  fetchSchoolSettings,
} from '../api/axios';
import { StudentGradesSection } from '../components/grading';
import { StudentFees } from '../components/StudentFees';
import { LoadingOverlay } from '../components/LoadingOverlay';
import { SHARED_SCHOOL_MOTTO } from '../schoolBranding';
import { formatClassLevelLabel } from '../utils/classLevelLabel';
import styles from './StudentDashboard.module.css';

type Section = 'dashboard' | 'assignments' | 'grades' | 'timetable' | 'announcements' | 'fees' | 'profile';
type AssignmentsView = 'main' | 'detail';
type AnnouncementTab = 'general' | 'class' | 'clubs';

interface Assignment {
  id: number;
  subject: string;
  title: string;
  dueDate: string;
  status: 'pending' | 'completed';
  description?: string;
  resources?: AssignmentResource[];
  teacherName?: string;
  className?: string;
}

export const StudentDashboard: React.FC = () => {
  const { user, logout, setUser } = useAuth();
  const { currentSchool, getSchoolInfo, isSwitchingSchool, schoolSwitchId, completeSchoolSwitch } = useSchool();
  const { isMobileMenuOpen, isMobileView, toggleMobileMenu, closeMobileMenu } = useMobileMenu();
  const schoolInfo = getSchoolInfo(currentSchool);
  const studentName = user?.first_name || user?.name || 'Student';
  const [activeSection, setActiveSection] = useState<Section>('dashboard');
  const [assignmentsView, setAssignmentsView] = useState<AssignmentsView>('main');
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);
  const [pendingExpanded, setPendingExpanded] = useState(true);
  const [completedExpanded, setCompletedExpanded] = useState(true);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [pendingAvatarFile, setPendingAvatarFile] = useState<File | null>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [announcements, setAnnouncements] = useState<AnnouncementDto[]>([]);
  const [loadingAnnouncements, setLoadingAnnouncements] = useState(false);
  const [classAnnouncements, setClassAnnouncements] = useState<ClassAnnouncementDto[]>([]);
  const [loadingClassAnnouncements, setLoadingClassAnnouncements] = useState(false);
  const [announcementTab, setAnnouncementTab] = useState<AnnouncementTab>('general');
  const [selectedClubFilter, setSelectedClubFilter] = useState<number | null>(null);
  const [userClubs, setUserClubs] = useState<ClubDto[]>([]);
  const announcementLastSeenKey = user?.id ? `announcementLastSeen:student:${user.id}` : null;
  const [timetables, setTimetables] = useState<TimetableDto[]>([]);
  const [loadingTimetables, setLoadingTimetables] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState<string>(user?.phone_number ?? '');
  const [emailAddress, setEmailAddress] = useState<string>((user as any)?.recovery_email ?? '');
  const [savingProfile, setSavingProfile] = useState(false);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [studentClass, setStudentClass] = useState<SchoolClassDto | null>(null);
  const [studentType, setStudentType] = useState<'day_scholar' | 'boarder' | ''>('');
  const [loadingStudentType, setLoadingStudentType] = useState(false);
  const [boardingLocked, setBoardingLocked] = useState(false);
  const [boardingAdminOverride, setBoardingAdminOverride] = useState(false);
  const [currentTermInfo, setCurrentTermInfo] = useState<{ year: string; term: number }>({ year: new Date().getFullYear().toString(), term: 1 });
  const getLastSeenAnnouncementTime = (): number => {
    if (!announcementLastSeenKey) return 0;
    const raw = window.localStorage.getItem(announcementLastSeenKey);
    const parsed = raw ? Number(raw) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const markAnnouncementsAsSeen = () => {
    if (!announcementLastSeenKey) return;
    window.localStorage.setItem(announcementLastSeenKey, String(Date.now()));
  };

  const countNewAnnouncements = (items: Array<{ created_at: string }>): number => {
    const lastSeen = getLastSeenAnnouncementTime();
    if (lastSeen <= 0) return 0;
    return items.reduce((count, item) => {
      const createdAt = new Date(item.created_at).getTime();
      return Number.isFinite(createdAt) && createdAt > lastSeen ? count + 1 : count;
    }, 0);
  };
  const newAnnouncementCount =
    countNewAnnouncements(announcements) +
    countNewAnnouncements(classAnnouncements);
  const bellBadgeCount = newAnnouncementCount > 99 ? '99+' : String(newAnnouncementCount);
  const renderLearnMore = (label: string, items: string[]) => (
    <div className={styles.learnMoreTrigger}>
      <button type="button" aria-label={label} className={styles.learnMoreButton}>?</button>
      <div className={styles.learnMoreTooltip}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>How to use this page</div>
        <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
          {items.map((item) => <li key={item}>{item}</li>)}
        </ul>
      </div>
    </div>
  );

  const seenSchoolFetchRef = useRef(false);
  const schoolSwitchTimeoutRef = useRef<number | null>(null);

  const formatTeacherLabel = (name?: string | null): string => {
    const base = name && name.trim().length > 0 ? name : 'No Teacher';
    // Strip anything after a dash or en dash (commonly used before year ranges)
    const [firstPart] = base.split(/[-–]/);
    return firstPart.trim();
  };

  const anySchoolLoading =
    loadingAnnouncements ||
    loadingClassAnnouncements ||
    loadingTimetables ||
    loadingStudentType;

  useEffect(() => {
    if (!isSwitchingSchool) {
      seenSchoolFetchRef.current = false;
      if (schoolSwitchTimeoutRef.current !== null) {
        window.clearTimeout(schoolSwitchTimeoutRef.current);
        schoolSwitchTimeoutRef.current = null;
      }
      return;
    }

    if (anySchoolLoading) {
      seenSchoolFetchRef.current = true;
    }

    if (seenSchoolFetchRef.current && !anySchoolLoading) {
      completeSchoolSwitch(schoolSwitchId);
      return;
    }

    if (schoolSwitchTimeoutRef.current === null) {
      schoolSwitchTimeoutRef.current = window.setTimeout(() => {
        completeSchoolSwitch(schoolSwitchId);
        schoolSwitchTimeoutRef.current = null;
      }, 6000);
    }
  }, [isSwitchingSchool, schoolSwitchId, anySchoolLoading, completeSchoolSwitch]);

  // Helper function to get the student's class display name
  const getClassDisplayName = (): string => {
    // If we have the class info loaded, use the actual class name
    if (studentClass) {
      return studentClass.name;
    }
    const formLevel = (user as any)?.form;
    if (!formLevel) return 'Not assigned';

    return formatClassLevelLabel(currentSchool, formLevel);
  };

  // Helper function to get current day name
  const getCurrentDay = (): string => {
    const dayMap: { [key: number]: string } = {
      0: 'sunday',
      1: 'monday',
      2: 'tuesday',
      3: 'wednesday',
      4: 'thursday',
      5: 'friday',
      6: 'saturday',
    };
    return dayMap[new Date().getDay()];
  };

  // Helper function to format time
  const formatTime = (timeStr: string): string => {
    if (!timeStr) return '';
    const timePart = timeStr.includes('T') 
      ? timeStr.split('T')[1]?.split(':').slice(0, 2).join(':') 
      : timeStr.split(':').slice(0, 2).join(':');
    return timePart || '';
  };

  // Helper function to get next class
  const getNextClass = () => {
    const currentDay = getCurrentDay();
    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    
    // Get today's classes
    const todayClasses = timetables
      .filter(t => t.day === currentDay)
      .sort((a, b) => {
        const timeA = formatTime(a.start_time || '');
        const timeB = formatTime(b.start_time || '');
        return timeA.localeCompare(timeB);
      });

    // Find next class today
    const nextToday = todayClasses.find(t => {
      const startTime = formatTime(t.start_time || '');
      return startTime > currentTime;
    });

    if (nextToday) return { entry: nextToday, isToday: true };

    // If no more classes today, get first class tomorrow
    const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const currentDayIndex = dayOrder.indexOf(currentDay);
    
    for (let i = 1; i <= 4; i++) {
      const nextDayIndex = (currentDayIndex + i) % 5;
      const nextDay = dayOrder[nextDayIndex];
      const nextDayClasses = timetables
        .filter(t => t.day === nextDay)
        .sort((a, b) => {
          const timeA = formatTime(a.start_time || '');
          const timeB = formatTime(b.start_time || '');
          return timeA.localeCompare(timeB);
        });
      
      if (nextDayClasses.length > 0) {
        return { entry: nextDayClasses[0], isToday: false, day: nextDay };
      }
    }

    return null;
  };

  // Dashboard View
  const DashboardContent = () => {
    const dayLabels: { [key: string]: string } = {
      'monday': 'Monday',
      'tuesday': 'Tuesday',
      'wednesday': 'Wednesday',
      'thursday': 'Thursday',
      'friday': 'Friday',
    };
    const currentDay = getCurrentDay();
    const todayClasses = timetables
      .filter(t => t.day === currentDay)
      .sort((a, b) => {
        const timeA = formatTime(a.start_time || '');
        const timeB = formatTime(b.start_time || '');
        return timeA.localeCompare(timeB);
      });
    const nextClass = getNextClass();

    return (
      <div className={styles.contentInner}>
        {/* Welcome Header */}
        <div className={styles.sectionHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 className={styles.sectionTitle}>Welcome back, {studentName.split(' ')[0]}! 👋</h1>
            {renderLearnMore('Learn more about Dashboard', [
              'Check cards for pending tasks, announcements, and class count.',
              'Use View All actions to jump into full timetable and announcements.',
              'Review next class details to prepare before lessons start.',
            ])}
          </div>
          <p className={styles.sectionSubtitle}>
            {schoolInfo.shortName} {studentClass || (user as any)?.form ? `• ${getClassDisplayName()}` : ''} • Here's what's happening today
          </p>
          <p className={styles.platformMotto}>{SHARED_SCHOOL_MOTTO}</p>
        </div>

        {/* Stats Grid */}
        <div className={styles.statsGrid}>
          <div className={styles.statCard} style={{ '--stat-gradient': currentSchool === 'TLC' ? '#15803d' : '#1e3a5f' } as React.CSSProperties}>
            <div className={styles.statCardHeader}>
              <div className={styles.statIcon}>📋</div>
            </div>
            <div className={styles.statValue}>{assignments.filter(a => a.status === 'pending').length}</div>
            <div className={styles.statLabel}>Pending Assignments</div>
          </div>
          
          <div className={styles.statCard} style={{ '--stat-gradient': '#059669' } as React.CSSProperties}>
            <div className={styles.statCardHeader}>
              <div className={styles.statIcon}>✅</div>
            </div>
            <div className={styles.statValue}>{assignments.filter(a => a.status === 'completed').length}</div>
            <div className={styles.statLabel}>Completed</div>
          </div>
          
          <div className={styles.statCard} style={{ '--stat-gradient': '#2563eb' } as React.CSSProperties}>
            <div className={styles.statCardHeader}>
              <div className={styles.statIcon}>📢</div>
            </div>
            <div className={styles.statValue}>{announcements.length}</div>
            <div className={styles.statLabel}>Announcements</div>
          </div>
          
          <div className={styles.statCard} style={{ '--stat-gradient': '#d97706' } as React.CSSProperties}>
            <div className={styles.statCardHeader}>
              <div className={styles.statIcon}>📅</div>
            </div>
            <div className={styles.statValue}>{todayClasses.length}</div>
            <div className={styles.statLabel}>Classes Today</div>
          </div>
        </div>

        {/* Two Column Layout: Next Class & Today's Schedule */}
        <div className={styles.twoColumnGrid}>
          {/* Next Class Card */}
          <div className={styles.nextClassCard}>
            <div style={{ fontSize: 14, opacity: 0.95, marginBottom: 12, fontWeight: 500 }}>
              📍 Next Class
            </div>
            {loadingTimetables ? (
              <div style={{ fontSize: 15, opacity: 0.9 }}>Loading...</div>
            ) : nextClass ? (
              <>
                <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>
                  {nextClass.entry.subject?.name || 'No Subject'}
                </div>
                <div style={{ fontSize: 14, opacity: 0.95, marginBottom: 12 }}>
                  {nextClass.entry.teacher?.name || 'No Teacher'}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
                  {nextClass.entry.start_time && nextClass.entry.end_time && (
                    <div style={{ fontSize: 15, fontWeight: 600 }}>
                      ⏰ {formatTime(nextClass.entry.start_time)} - {formatTime(nextClass.entry.end_time)}
                    </div>
                  )}
                  {nextClass.entry.room && (
                    <div style={{ fontSize: 14, opacity: 0.95 }}>
                      🏫 Room {nextClass.entry.room}
                    </div>
                  )}
                  <div style={{ fontSize: 13, opacity: 0.9, marginTop: 4 }}>
                    {nextClass.isToday ? 'Today' : `On ${dayLabels[nextClass.day || '']}`}
                  </div>
                </div>
              </>
            ) : (
              <div style={{ fontSize: 15, opacity: 0.9 }}>
                No upcoming classes scheduled
              </div>
            )}
          </div>

          {/* Today's Schedule Card */}
          <div className={styles.contentCard}>
            <div className={styles.cardHeader}>
              <h3 className={styles.cardTitle}>
                <span className={styles.cardTitleIcon}>📅</span>
                Today's Schedule
              </h3>
              <button 
                className={styles.cardAction}
                onClick={() => setActiveSection('timetable')}
              >
                View All →
              </button>
            </div>
            <div className={styles.cardBody}>
              {loadingTimetables ? (
                <div className={styles.loading}>
                  <div className={styles.loadingSpinner}></div>
                  Loading...
                </div>
              ) : todayClasses.length === 0 ? (
                <div className={styles.emptyState}>
                  <div className={styles.emptyStateIcon}>🎉</div>
                  <div className={styles.emptyStateTitle}>No classes today!</div>
                  <div className={styles.emptyStateDescription}>Enjoy your free day.</div>
                </div>
              ) : (
                <div className={styles.itemList}>
                  {todayClasses.slice(0, 4).map((entry) => {
                    const isNextClass = nextClass?.entry.id === entry.id && nextClass.isToday;
                    return (
                      <div key={entry.id} className={`${styles.listItem} ${isNextClass ? styles.timetableClass + ' ' + styles.isNext : ''}`}>
                        <div className={styles.listItemIcon}>📚</div>
                        <div className={styles.listItemContent}>
                          <div className={styles.listItemTitle}>{entry.subject?.name || 'No Subject'}</div>
                          <div className={styles.listItemMeta}>
                            <span>👤 {entry.teacher?.name || 'No Teacher'}</span>
                            {entry.room && <span>🏫 {entry.room}</span>}
                          </div>
                        </div>
                        <div className={styles.timetableTime}>
                          {formatTime(entry.start_time || '')}
                        </div>
                        {isNextClass && <span className={`${styles.badge} ${styles.upcoming}`}>Next</span>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Announcements */}
        <div className={styles.contentCard}>
          <div className={styles.cardHeader}>
            <h3 className={styles.cardTitle}>
              <span className={styles.cardTitleIcon}>📢</span>
              Recent Announcements
            </h3>
            {announcements.length > 3 && (
              <button 
                className={styles.cardAction}
                onClick={() => setActiveSection('announcements')}
              >
                View All →
              </button>
            )}
          </div>
          <div className={styles.cardBody}>
            {loadingAnnouncements ? (
              <div className={styles.loading}>
                <div className={styles.loadingSpinner}></div>
                Loading announcements...
              </div>
            ) : announcements.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyStateIcon}>📭</div>
                <div className={styles.emptyStateTitle}>No announcements</div>
                <div className={styles.emptyStateDescription}>You're all caught up!</div>
              </div>
            ) : (
              <div className={styles.itemList}>
                {announcements.slice(0, 3).map((announcement) => (
                  <div 
                    key={announcement.id} 
                    className={styles.announcementCard}
                    onClick={() => setActiveSection('announcements')}
                  >
                    <div className={styles.announcementHeader}>
                      <div className={styles.announcementIcon}>📢</div>
                      <div className={styles.announcementContent}>
                        <div className={styles.announcementTitle}>{announcement.title}</div>
                        <div className={styles.announcementTime}>{formatTimeAgo(announcement.created_at)}</div>
                      </div>
                      <span className={styles.announcementBadge} style={{
                        background: announcement.target_group === 'all' ? 'rgba(13, 148, 136, 0.1)' : 'rgba(6, 182, 212, 0.1)',
                        color: announcement.target_group === 'all' ? '#0d9488' : '#06b6d4',
                      }}>
                        {announcement.target_group}
                      </span>
                    </div>
                    <div className={styles.announcementMessage}>
                      {announcement.message.length > 150 
                        ? `${announcement.message.substring(0, 150)}...` 
                        : announcement.message}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const getCompletedAssignmentsKey = () => {
    const userId = user?.id ?? 'anonymous';
    return `studentCompletedAssignments:${currentSchool}:${userId}`;
  };

  const formatDueDate = (value: string) => {
    if (!value) return '';
    const [datePart] = value.split('T');
    return datePart;
  };

  const loadAssignments = async () => {
    try {
      const data = await fetchAssignments({ school: currentSchool });

      let completedIds: number[] = [];
      if (typeof window !== 'undefined') {
        try {
          const raw = window.localStorage.getItem(getCompletedAssignmentsKey());
          if (raw) {
            const parsed = JSON.parse(raw) as number[];
            if (Array.isArray(parsed)) completedIds = parsed;
          }
        } catch {
          // ignore storage issues
        }
      }

      const mapped: Assignment[] = data.map((a: AssignmentDto) => ({
        id: a.id,
        subject: a.subject,
        title: a.title,
        dueDate: a.due_date,
        status: completedIds.includes(a.id) ? 'completed' : 'pending',
        description: a.description || undefined,
        teacherName: a.teacher_name,
        resources: (a.resources || undefined) as AssignmentResource[] | undefined,
      }));
      setAssignments(mapped);
    } catch (err) {
      console.error('Failed to load assignments for student', err);
    }
  };

  // Load assignments when grades section is accessed
  const loadAssignmentsForGrades = async () => {
    try {
      const data = await fetchAssignments({ school: currentSchool });
      const mapped: Assignment[] = data.map((a: AssignmentDto) => ({
        id: a.id,
        subject: a.subject,
        title: a.title,
        dueDate: a.due_date,
        status: 'pending',
        description: a.description || undefined,
        resources: (a.resources || undefined) as AssignmentResource[] | undefined,
        teacherName: a.teacher_name,
        className: a.class_name || undefined,
      }));
      setAssignments(mapped);
    } catch (err) {
      console.error('Failed to load assignments', err);
    }
  };

  const viewAssignment = (assignment: Assignment) => {
    setSelectedAssignment(assignment);
    setAssignmentsView('detail');
  };

  useEffect(() => {
    if (user?.avatar_url) {
      setAvatarPreview(user.avatar_url);
    } else {
      setAvatarPreview(null);
    }
    setPendingAvatarFile(null);
  }, [user?.id, user?.avatar_url]);

  const loadAnnouncements = async (view: 'general' | 'clubs' = announcementTab === 'clubs' ? 'clubs' : 'general', clubId: number | null = selectedClubFilter) => {
    setLoadingAnnouncements(true);
    try {
      const apiType = view === 'general' ? 'regular' : 'club';
      const data = await fetchAnnouncements({
        type: apiType,
        club_id: clubId || undefined,
        school: currentSchool
      });
      setAnnouncements(data);
    } catch (error) {
      console.error('Failed to load announcements', error);
    } finally {
      setLoadingAnnouncements(false);
    }
  };

  const loadClassAnnouncementsForStudent = async () => {
    setLoadingClassAnnouncements(true);
    try {
      const data = await fetchClassAnnouncementsForStudent();
      setClassAnnouncements(data);
    } catch (error) {
      console.error('Failed to load class announcements for student', error);
    } finally {
      setLoadingClassAnnouncements(false);
    }
  };

  const loadClubs = async () => {
    try {
      const data = await fetchClubs({ school: currentSchool });
      setUserClubs(data);
    } catch (error) {
      console.error('Failed to load clubs', error);
    }
  };

  // Load student's class information
  const loadStudentClass = async () => {
    const studentFormLevel = (user as any)?.form;
    if (!studentFormLevel) return;
    
    try {
      const classes = await fetchActiveClasses({ school: currentSchool });
      // Find the class that matches the student's form/level
      const matchingClass = classes.find(c => c.level === studentFormLevel);
      if (matchingClass) {
        setStudentClass(matchingClass);
      }
    } catch (error) {
      console.error('Failed to load student class', error);
    }
  };

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Posted just now';
    if (diffMins < 60) return `Posted ${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `Posted ${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays === 1) return 'Posted 1 day ago';
    if (diffDays < 30) return `Posted ${diffDays} days ago`;
    return `Posted on ${date.toLocaleDateString()}`;
  };

  useEffect(() => {
    const loadSettingsAndProfile = async () => {
      let year = new Date().getFullYear().toString();
      let term = 1;
      try {
        const s = await fetchSchoolSettings(currentSchool);
        year = s.current_academic_year;
        term = s.current_term;
        setCurrentTermInfo({ year, term });
      } catch {
        // fall back to defaults
      }
      await loadStudentTermProfile(year, term);
    };
    void loadSettingsAndProfile();
    loadAssignments();
    loadAnnouncements();
    loadTimetables();
    loadClubs();
    loadStudentClass();
    loadClassAnnouncementsForStudent();
  }, [currentSchool]);

  const loadStudentTermProfile = async (year?: string, termInput?: number) => {
    if (!user) return;

    const academicYear = year ?? currentTermInfo.year;
    const term = termInput ?? currentTermInfo.term;

    setLoadingStudentType(true);
    try {
      const res = await fetchStudentTermProfile({ academic_year: academicYear, term });
      if (res.profile?.boarding_status === 'day_scholar' || res.profile?.boarding_status === 'boarder') {
        setStudentType(res.profile.boarding_status);
      } else {
        setStudentType('');
      }
      setBoardingLocked((res as any).boarding_locked ?? false);
      setBoardingAdminOverride((res.profile as any)?.admin_override ?? false);
    } catch {
      setStudentType('');
    } finally {
      setLoadingStudentType(false);
    }
  };

  const loadTimetables = async () => {
    if (!user || !(user as any).form) return;
    setLoadingTimetables(true);
    try {
      const data = await fetchTimetables((user as any).form, undefined, 'published', currentSchool);
      setTimetables(data);
    } catch (error) {
      console.error('Failed to load timetables', error);
    } finally {
      setLoadingTimetables(false);
    }
  };

  useEffect(() => {
    if (activeSection === 'assignments') {
      loadAssignments();
    }
    if (activeSection === 'announcements') {
      loadClassAnnouncementsForStudent();
      if (announcementTab === 'general') {
        loadAnnouncements('general');
      } else if (announcementTab === 'clubs') {
        loadAnnouncements('clubs', selectedClubFilter);
      }
    }
    if (activeSection === 'timetable') {
      loadTimetables();
    }
    if (activeSection === 'grades') {
      loadAssignmentsForGrades();
    }
  }, [activeSection]);

  useEffect(() => {
    if (activeSection !== 'announcements') return;

    if (announcementTab === 'general') {
      loadAnnouncements('general');
    } else if (announcementTab === 'clubs') {
      loadAnnouncements('clubs', selectedClubFilter);
    }
  }, [announcementTab, selectedClubFilter, activeSection]);

  useEffect(() => {
    if (activeSection !== 'announcements') return;
    markAnnouncementsAsSeen();
  }, [activeSection, announcements, classAnnouncements]);

  const handleAvatarChange: React.ChangeEventHandler<HTMLInputElement> = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 2 * 1024 * 1024) {
      setAvatarError('File size must be less than 2MB');
      return;
    }
    
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const validExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    
    if (!validTypes.includes(file.type) && (!fileExtension || !validExtensions.includes(fileExtension))) {
      setAvatarError('File must be an image (JPEG, PNG, GIF, or WebP)');
      return;
    }
    
    setAvatarError(null);
    const localUrl = URL.createObjectURL(file);
    setAvatarPreview(localUrl);
    setPendingAvatarFile(file);
  };

  const handleAvatarSave = async () => {
    if (!pendingAvatarFile) return;
    const currentPreview = avatarPreview;
    setAvatarError(null);
    try {
      const updatedUser = await uploadAvatar(pendingAvatarFile);
      setUser(updatedUser);
      setAvatarPreview(updatedUser.avatar_url ?? currentPreview);
      setPendingAvatarFile(null);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 
                          err?.response?.data?.errors?.avatar?.[0] ||
                          'Failed to upload avatar. Please try again.';
      setAvatarError(errorMessage);
      console.error('Failed to update avatar', err);
    }
  };

  const calculateAge = (dateOfBirth: string | undefined): number | null => {
    if (!dateOfBirth) return null;
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  useEffect(() => {
    if (user?.phone_number !== undefined) {
      setPhoneNumber(user.phone_number || '');
    }
    const primaryEmail =
      (user as any)?.email ??
      (user as any)?.recovery_email ??
      '';
    setEmailAddress(primaryEmail);
  }, [user?.id, user?.phone_number, (user as any)?.email, (user as any)?.recovery_email]);

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      // First update basic profile fields
      let finalUser = await updateUserProfile({
        phone_number: phoneNumber || undefined,
        email: emailAddress || undefined,
      });
      // If there is a pending avatar, upload it as part of saving changes
      if (pendingAvatarFile) {
        try {
          const avatarUser = await uploadAvatar(pendingAvatarFile);
          finalUser = avatarUser;
          setAvatarPreview(avatarUser.avatar_url ?? avatarPreview);
          setPendingAvatarFile(null);
        } catch (err: any) {
          console.error('Failed to upload avatar during profile save', err);
          const errorMessage =
            err?.response?.data?.message ||
            err?.response?.data?.errors?.avatar?.[0] ||
            'Profile saved, but failed to upload avatar. Please try again.';
          setAvatarError(errorMessage);
          window.alert(errorMessage);
        }
      }

      setUser(finalUser);
      if (finalUser.phone_number !== undefined) {
        setPhoneNumber(finalUser.phone_number || '');
      }
      const updatedPrimaryEmail =
        (finalUser as any)?.email ??
        (finalUser as any)?.recovery_email ??
        '';
      setEmailAddress(updatedPrimaryEmail);

      if (studentType === 'day_scholar' || studentType === 'boarder') {
        await saveStudentTermProfile({
          academic_year: currentTermInfo.year,
          term: currentTermInfo.term,
          boarding_status: studentType,
        });
      }

      window.alert('Profile updated successfully!');
    } catch (err: any) {
      console.error('Failed to update profile', err);
      const errorMessage = err.response?.data?.message || 'Failed to update profile. Please try again.';
      window.alert(errorMessage);
    } finally {
      setSavingProfile(false);
    }
  };

  const markAssignmentAsDone = (assignment: Assignment) => {
    const todayStr = new Date().toISOString().split('T')[0];
    if (formatDueDate(assignment.dueDate) < todayStr) {
      return;
    }

    const updatedAssignments: Assignment[] = assignments.map((a) => {
      if (a.id !== assignment.id) return a;
      return { ...a, status: 'completed' as Assignment['status'] };
    });

    setAssignments(updatedAssignments);
    setSelectedAssignment({ ...assignment, status: 'completed' });

    if (typeof window !== 'undefined') {
      try {
        const raw = window.localStorage.getItem(getCompletedAssignmentsKey());
        let completedIds: number[] = [];
        if (raw) {
          const parsed = JSON.parse(raw) as number[];
          if (Array.isArray(parsed)) completedIds = parsed;
        }
        if (!completedIds.includes(assignment.id)) {
          completedIds.push(assignment.id);
        }
        window.localStorage.setItem(getCompletedAssignmentsKey(), JSON.stringify(completedIds));
      } catch {
        // ignore storage errors
      }
    }
  };

  // Assignments View
  const AssignmentsContent = () => {
    if (assignmentsView === 'detail' && selectedAssignment) {
      const docsForAssignment: AssignmentResource[] = (selectedAssignment as any).resources || [];
      const todayStr = new Date().toISOString().split('T')[0];
      const isPastDue = formatDueDate(selectedAssignment.dueDate) < todayStr;

      return (
        <div className={styles.contentInner}>
          <div className={styles.sectionHeader}>
            <h1 className={styles.sectionTitle} style={{ wordBreak: 'break-word' }}>
              <span 
                style={{ cursor: 'pointer', color: '#0d9488' }} 
                onClick={() => { setAssignmentsView('main'); setSelectedAssignment(null); }}
              >
                Assignments
              </span>
              <span style={{ color: '#94a3b8', margin: '0 12px' }}>→</span>
              {selectedAssignment.subject}
            </h1>
            <p className={styles.sectionSubtitle}>Assignment details and instructions</p>
          </div>

          {/* Assignment Header Card */}
          <div className={styles.nextClassCard} style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 28, fontWeight: 700, marginBottom: 12 }}>{selectedAssignment.subject}</div>
            <div style={{ marginBottom: 8, fontSize: 18, opacity: 0.95 }}>{selectedAssignment.title}</div>
            <div style={{ fontSize: 14, marginBottom: 6, opacity: 0.9, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>
                <strong>Due Date:</strong> {formatDueDate(selectedAssignment.dueDate)}
              </span>
              {isPastDue && selectedAssignment.status === 'pending' && (
                <span className={`${styles.badge} ${styles.overdue}`}>Past due</span>
              )}
              {!isPastDue && selectedAssignment.status === 'pending' && (
                <span className={`${styles.badge} ${styles.pending}`}>Pending</span>
              )}
            </div>
            <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 16 }}>
              <strong>Teacher:</strong> {selectedAssignment.teacherName || 'Not assigned'}
            </div>
            {selectedAssignment.status === 'pending' ? (
              <button
                type="button"
                onClick={() => {
                  if (!isPastDue) {
                    markAssignmentAsDone(selectedAssignment);
                  }
                }}
                className={styles.secondaryButton}
                disabled={isPastDue}
              >
                {isPastDue ? 'Past due – completion locked' : '✓ Mark as Completed'}
              </button>
            ) : (
              <button
                type="button"
                className={`${styles.secondaryButton} ${styles.completed}`}
                disabled
              >
                ✓ Completed
              </button>
            )}
          </div>

          {/* Instructions Card */}
          <div className={styles.contentCard}>
            <div className={styles.cardHeader}>
              <h3 className={styles.cardTitle}>
                <span className={styles.cardTitleIcon}>📝</span>
                Assignment Instructions
              </h3>
            </div>
            <div className={styles.cardBody}>
              <p style={{ color: '#475569', fontSize: 15, lineHeight: 1.8, marginBottom: 20 }}>
                <strong style={{ color: '#0f172a' }}>Description:</strong>{' '}
                {selectedAssignment.description || `Complete the ${selectedAssignment.title} assignment.`}
              </p>
              
              {docsForAssignment.length > 0 && (
                <div>
                  <h4 style={{ marginBottom: 16, color: '#0f172a' }}>Attached Documents</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                    {docsForAssignment.map((doc) => {
                      const ext = doc.original_name.split('.').pop()?.toUpperCase() ?? 'FILE';
                      const isImage = doc.mime_type?.startsWith('image/');
                      const isPdf = doc.mime_type === 'application/pdf';
                      return (
                        <div
                          key={doc.id}
                          style={{
                            width: '100%',
                            maxWidth: 280,
                            borderRadius: 10,
                            border: '1px solid #e2e8f0',
                            background: '#fff',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          <div
                            style={{
                              height: 200,
                              background: '#f8fafc',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                            }}
                          >
                            {isImage ? (
                              <img src={doc.url} alt={doc.original_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : isPdf ? (
                              <iframe
                                src={doc.url}
                                title={doc.original_name}
                                style={{ width: '100%', height: '100%', border: 'none' }}
                              />
                            ) : (
                              <div style={{ textAlign: 'center', fontSize: 24, fontWeight: 700, color: '#0d9488' }}>{ext}</div>
                            )}
                          </div>
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              padding: '12px 16px',
                              fontSize: 13,
                              color: '#0d9488',
                              textDecoration: 'none',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              fontWeight: 500,
                            }}
                            title={doc.original_name}
                          >
                            {doc.original_name}
                          </a>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    const todayStr = new Date().toISOString().split('T')[0];
    const pendingAssignmentsRaw = assignments.filter(a => a.status === 'pending');
    const overdueAssignments = pendingAssignmentsRaw.filter(a => formatDueDate(a.dueDate) < todayStr);
    const upcomingAssignments = pendingAssignmentsRaw.filter(a => formatDueDate(a.dueDate) >= todayStr);
    const completedAssignments = assignments.filter(a => a.status === 'completed');

    return (
      <div className={styles.contentInner}>
        <div className={styles.sectionHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 className={styles.sectionTitle}>Assignments 📝</h1>
            {renderLearnMore('Learn more about Assignments', [
              'Open an assignment card to view full instructions and attachments.',
              'Mark work as completed once submitted to your teacher.',
              'Check overdue and upcoming groups to prioritize tasks.',
            ])}
          </div>
          <p className={styles.sectionSubtitle}>View and manage your assignments</p>
        </div>
        
        {/* Pending Assignments Section */}
        <div 
          className={styles.collapsibleHeader}
          onClick={() => setPendingExpanded(!pendingExpanded)}
        >
          <span className={styles.collapsibleTitle}>
            📋 Pending Assignments
            <span className={styles.collapsibleCount}>{pendingAssignmentsRaw.length}</span>
          </span>
          <span className={`${styles.collapsibleIcon} ${pendingExpanded ? styles.expanded : ''}`}>▼</span>
        </div>
        
        {pendingExpanded && (
          <div className={styles.itemList} style={{ marginBottom: 32 }}>
            {pendingAssignmentsRaw.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyStateIcon}>🎉</div>
                <div className={styles.emptyStateTitle}>All caught up!</div>
                <div className={styles.emptyStateDescription}>No pending assignments.</div>
              </div>
            ) : (
              <>
                {overdueAssignments.length > 0 && (
                  <div style={{ width: '100%', marginBottom: 24 }}>
                    <h3 className={styles.sectionSubtitle} style={{ marginBottom: 12 }}>
                      ⏰ Past Due
                    </h3>
                    {overdueAssignments.map((assignment) => (
                      <div key={assignment.id} className={styles.listItem} onClick={() => viewAssignment(assignment)}>
                        <div className={styles.listItemIcon}>⚠️</div>
                        <div className={styles.listItemContent}>
                          <div className={styles.listItemTitle}>
                            {assignment.subject}{' '}
                            <span className={`${styles.badge} ${styles.overdue}`} style={{ marginLeft: 8 }}>
                              Past due
                            </span>
                          </div>
                          <div className={styles.listItemMeta}>
                            <span>{assignment.title}</span>
                            <span>Due: {formatDueDate(assignment.dueDate)}</span>
                          </div>
                        </div>
                        <button
                          className={styles.primaryButton}
                          onClick={(e) => { e.stopPropagation(); viewAssignment(assignment); }}
                        >
                          View Details →
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {upcomingAssignments.length > 0 && (
                  <div style={{ width: '100%' }}>
                    <h3 className={styles.sectionSubtitle} style={{ marginBottom: 12 }}>
                      📅 Upcoming
                    </h3>
                    {upcomingAssignments.map((assignment) => (
                      <div key={assignment.id} className={styles.listItem} onClick={() => viewAssignment(assignment)}>
                        <div className={styles.listItemIcon}>📚</div>
                        <div className={styles.listItemContent}>
                          <div className={styles.listItemTitle}>{assignment.subject}</div>
                          <div className={styles.listItemMeta}>
                            <span>{assignment.title}</span>
                            <span>Due: {formatDueDate(assignment.dueDate)}</span>
                          </div>
                        </div>
                        <button
                          className={styles.primaryButton}
                          onClick={(e) => { e.stopPropagation(); viewAssignment(assignment); }}
                        >
                          View Details →
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Completed Assignments Section */}
        <div 
          className={styles.collapsibleHeader}
          onClick={() => setCompletedExpanded(!completedExpanded)}
          style={{ background: '#f0fdf4' }}
        >
          <span className={styles.collapsibleTitle}>
            ✅ Completed Assignments
            <span className={styles.collapsibleCount} style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#059669' }}>
              {completedAssignments.length}
            </span>
          </span>
          <span className={`${styles.collapsibleIcon} ${completedExpanded ? styles.expanded : ''}`}>▼</span>
        </div>

        {completedExpanded && (
          <div className={styles.itemList}>
            {completedAssignments.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyStateIcon}>📭</div>
                <div className={styles.emptyStateTitle}>No completed assignments</div>
                <div className={styles.emptyStateDescription}>Complete some assignments to see them here.</div>
              </div>
            ) : (
              completedAssignments.map((assignment) => (
                <div key={assignment.id} className={styles.listItem} onClick={() => viewAssignment(assignment)}>
                  <div className={styles.listItemIcon}>✅</div>
                  <div className={styles.listItemContent}>
                    <div className={styles.listItemTitle}>{assignment.subject}</div>
                    <div className={styles.listItemMeta}>
                      <span>{assignment.title}</span>
                      <span>Due: {formatDueDate(assignment.dueDate)}</span>
                    </div>
                  </div>
                  <span className={`${styles.badge} ${styles.completed}`}>Completed</span>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    );
  };

  // Grades View
  const GradesContent = () => {
    return (
      <div className={styles.contentInner}>
        <div className={styles.sectionHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 className={styles.sectionTitle}>My Grades 📈</h1>
            {renderLearnMore('Learn more about My Grades', [
              'Switch between exam and assignment tabs to view each result type.',
              'Use term selection to load results from previous academic terms.',
              'Exam grades appear only after admin publishing.',
            ])}
          </div>
          <p className={styles.sectionSubtitle}>View your academic performance</p>
        </div>
        <StudentGradesSection />
      </div>
    );
  };

  // Timetable View
  const TimetableContent = () => {
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const dayLabels = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

    return (
      <div className={styles.contentInner}>
        <div className={styles.sectionHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 className={styles.sectionTitle}>Weekly Timetable 📅</h1>
            {renderLearnMore('Learn more about Weekly Timetable', [
              'Review each day card to see subject, teacher, and room details.',
              'Use this page to plan books and materials for upcoming lessons.',
              'If empty, wait for the school to publish your timetable.',
            ])}
          </div>
          <p className={styles.sectionSubtitle}>
            {studentClass || (user as any)?.form ? `${getClassDisplayName()} Schedule` : 'Your Weekly Schedule'} • View your classes and schedule
          </p>
        </div>

        {loadingTimetables ? (
          <div className={styles.loading}>
            <div className={styles.loadingSpinner}></div>
            Loading timetable...
          </div>
        ) : timetables.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateIcon}>📅</div>
            <div className={styles.emptyStateTitle}>No Timetable Available</div>
            <div className={styles.emptyStateDescription}>
              Your timetable hasn't been published yet. Please check back later.
            </div>
          </div>
        ) : (
          <div className={styles.timetableGrid}>
            {days.map((day, dayIdx) => {
              const dayClasses = timetables
                .filter(t => t.day === day)
                .sort((a, b) => (a.period || 0) - (b.period || 0));

              if (dayClasses.length === 0) return null;

              return (
                <div key={day} className={styles.timetableDay}>
                  <div className={styles.timetableDayHeader}>
                    <span>📆</span> {dayLabels[dayIdx]}
                  </div>
                  <div className={styles.timetableClasses}>
                    {dayClasses.map((entry) => {
                      const nextClass = getNextClass();
                      const isNextClass = nextClass?.entry.id === entry.id && nextClass.isToday;
                      
                      return (
                        <div 
                          key={entry.id} 
                          className={`${styles.timetableClass} ${isNextClass ? styles.isNext : ''}`}
                        >
                          <div className={styles.timetableTime}>
                            {formatTime(entry.start_time || '')} - {formatTime(entry.end_time || '')}
                          </div>
                          <div className={styles.timetableDetails}>
                            <div className={styles.timetableSubject}>{entry.subject?.name || 'No Subject'}</div>
                            <div className={styles.timetableInfo}>
                              👤 {formatTeacherLabel(entry.teacher?.name)}
                            </div>
                          </div>
                          {entry.room && (
                            <div className={styles.timetableRoom}>🏫 {entry.room}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Announcements View
  const AnnouncementsContent = () => {
    const sortedClassAnnouncements = [...classAnnouncements].sort((a, b) => {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

    return (
      <div className={styles.contentInner}>
        <div className={styles.sectionHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 className={styles.sectionTitle}>Announcements 📢</h1>
            {renderLearnMore('Learn more about Announcements', [
              'General tab shows school-wide posts (not club-only messages).',
              'Club announcements from your school appear under the Clubs tab (optionally filter by club).',
              'Use the Class tab for messages from your teachers for your class.',
            ])}
          </div>
          <p className={styles.sectionSubtitle}>
            General, class, and club feeds are separate—club posts are not mixed into General.
          </p>
        </div>

        {/* Tabs */}
        <div className={styles.tabContainer} role="tablist" aria-label="Announcements categories">
          <button
            type="button"
            role="tab"
            aria-selected={announcementTab === 'general'}
            className={`${styles.tab} ${announcementTab === 'general' ? styles.active : ''}`}
            onClick={() => setAnnouncementTab('general')}
          >
            📢 General
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={announcementTab === 'class'}
            className={`${styles.tab} ${announcementTab === 'class' ? styles.active : ''}`}
            onClick={() => setAnnouncementTab('class')}
          >
            📚 Class
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={announcementTab === 'clubs'}
            className={`${styles.tab} ${announcementTab === 'clubs' ? styles.active : ''}`}
            onClick={() => setAnnouncementTab('clubs')}
          >
            🏆 Clubs
          </button>
        </div>

        {/* Tab Content */}
        {announcementTab === 'class' && (
          <div style={{ marginTop: 24 }}>
            <h2 className={styles.sectionTitle} style={{ fontSize: 20 }}>
              📚 Class Announcements
            </h2>
            <p className={styles.sectionSubtitle}>
              Messages shared with all your classes/forms
            </p>

            {loadingClassAnnouncements ? (
              <div className={styles.loading}>
                <div className={styles.loadingSpinner}></div>
                Loading class announcements...
              </div>
            ) : sortedClassAnnouncements.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyStateIcon}>📭</div>
                <div className={styles.emptyStateTitle}>No class announcements</div>
                <div className={styles.emptyStateDescription}>
                  Your teachers haven&apos;t posted any class announcements yet.
                </div>
              </div>
            ) : (
              <div className={styles.itemList}>
                {sortedClassAnnouncements.map((announcement) => (
                  <div key={announcement.id} className={styles.announcementCard}>
                    <div className={styles.announcementHeader}>
                      <div className={styles.announcementIcon}>📚</div>
                      <div className={styles.announcementContent}>
                        <div className={styles.announcementTitle}>{announcement.title}</div>
                        <div className={styles.announcementTime}>
                          {formatTimeAgo(announcement.created_at)}
                        </div>
                      </div>
                      {announcement.creator && (
                        <span className={styles.announcementBadge}>
                          {announcement.creator.name}
                        </span>
                      )}
                    </div>
                    <div className={styles.announcementMessage}>{announcement.message}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {announcementTab === 'general' && (
          <div style={{ marginTop: 24 }}>
            <h2 className={styles.sectionTitle} style={{ fontSize: 20 }}>
              🏫 School Announcements
            </h2>
            <p className={styles.sectionSubtitle}>
              General updates and notices shared with the whole school
            </p>

            {loadingAnnouncements ? (
              <div className={styles.loading}>
                <div className={styles.loadingSpinner}></div>
                Loading announcements...
              </div>
            ) : announcements.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyStateIcon}>📭</div>
                <div className={styles.emptyStateTitle}>No announcements</div>
                <div className={styles.emptyStateDescription}>
                  Check back later for new school announcements.
                </div>
              </div>
            ) : (
              <div className={styles.itemList}>
                {announcements.map((announcement) => (
                  <div key={announcement.id} className={styles.announcementCard}>
                    <div className={styles.announcementHeader}>
                      <div className={styles.announcementIcon}>📢</div>
                      <div className={styles.announcementContent}>
                        <div className={styles.announcementTitle}>{announcement.title}</div>
                        <div className={styles.announcementTime}>
                          {formatTimeAgo(announcement.created_at)}
                        </div>
                      </div>
                      <span
                        className={styles.announcementBadge}
                        style={{
                          background:
                            announcement.target_group === 'all'
                              ? 'rgba(13, 148, 136, 0.1)'
                              : 'rgba(6, 182, 212, 0.1)',
                          color: announcement.target_group === 'all' ? '#0d9488' : '#06b6d4',
                        }}
                      >
                        {announcement.target_group}
                      </span>
                    </div>
                    <div className={styles.announcementMessage}>{announcement.message}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {announcementTab === 'clubs' && (
          <div style={{ marginTop: 24 }}>
            <h2 className={styles.sectionTitle} style={{ fontSize: 20 }}>
              🏆 Club Announcements
            </h2>
            <p className={styles.sectionSubtitle}>
              Updates and notices from your school clubs
            </p>

            {/* Club Filter */}
            {userClubs.length > 0 && (
              <div className={styles.formGroup} style={{ maxWidth: 300 }}>
                <label className={styles.formLabel}>Filter by club</label>
                <select
                  value={selectedClubFilter || ''}
                  onChange={(e) => setSelectedClubFilter(e.target.value ? Number(e.target.value) : null)}
                  className={styles.formSelect}
                >
                  <option value="">All Clubs</option>
                  {userClubs.map((club) => (
                    <option key={club.id} value={club.id}>
                      {club.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {userClubs.length === 0 && (
              <div className={styles.emptyState}>
                <div className={styles.emptyStateIcon}>🏆</div>
                <div className={styles.emptyStateTitle}>No clubs available yet</div>
                <div className={styles.emptyStateDescription}>
                  Club announcements will appear here once clubs have been created and you&apos;re
                  added to them.
                </div>
              </div>
            )}

            {userClubs.length > 0 && (
              <>
                {loadingAnnouncements ? (
                  <div className={styles.loading}>
                    <div className={styles.loadingSpinner}></div>
                    Loading club announcements...
                  </div>
                ) : announcements.length === 0 ? (
                  <div className={styles.emptyState}>
                    <div className={styles.emptyStateIcon}>📭</div>
                    <div className={styles.emptyStateTitle}>No club announcements</div>
                    <div className={styles.emptyStateDescription}>
                      There are currently no announcements for your clubs.
                    </div>
                  </div>
                ) : (
                  <div className={styles.itemList}>
                    {announcements.map((announcement) => (
                      <div key={announcement.id} className={styles.announcementCard}>
                        {announcement.club && (
                          <span
                            className={`${styles.badge} ${styles.completed}`}
                            style={{ marginBottom: 12 }}
                          >
                            🏆 {announcement.club.name}
                          </span>
                        )}
                        <div className={styles.announcementHeader}>
                          <div className={styles.announcementIcon}>📢</div>
                          <div className={styles.announcementContent}>
                            <div className={styles.announcementTitle}>{announcement.title}</div>
                            <div className={styles.announcementTime}>
                              {formatTimeAgo(announcement.created_at)}
                            </div>
                          </div>
                        </div>
                        <div className={styles.announcementMessage}>{announcement.message}</div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  // Profile View
  const ProfileContent = () => (
    <div className={styles.contentInner}>
      <div className={styles.sectionHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h1 className={styles.sectionTitle}>My Profile 👤</h1>
          {renderLearnMore('Learn more about My Profile', [
            'Keep phone and email updated for communication and recovery.',
            'Set the correct student type to enable accurate fee records.',
            'Save profile changes after updating personal information.',
          ])}
        </div>
        <p className={styles.sectionSubtitle}>Manage your personal information</p>
      </div>

      <div className={styles.profileSection}>
        {/* Avatar Section */}
        <div className={styles.avatarSection}>
          <div className={styles.avatarWrapper}>
            {avatarPreview ? (
              <img src={avatarPreview} alt="Profile" className={styles.avatarPreview} />
            ) : (
              <div className={styles.avatarPlaceholder}>
                {studentName.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div className={styles.avatarActions}>
            <div className={styles.formGroup} style={{ marginBottom: 0 }}>
              <label className={styles.formLabel}>Change profile picture</label>
              <input type="file" accept="image/*" onChange={handleAvatarChange} />
              <div className={styles.formHint}>Accepted: JPEG, PNG, GIF, WebP (max 2MB)</div>
            </div>
            {avatarError && (
              <div style={{ fontSize: 12, color: '#ef4444', marginTop: 8 }}>{avatarError}</div>
            )}
            <button
              type="button"
              onClick={handleAvatarSave}
              disabled={!pendingAvatarFile}
              className={styles.primaryButton}
              style={{ marginTop: 12, opacity: pendingAvatarFile ? 1 : 0.5 }}
            >
              Save Profile Picture
            </button>
          </div>
        </div>

        {/* Profile Form */}
        <div className={styles.contentCard}>
          <div className={styles.cardHeader}>
            <h3 className={styles.cardTitle}>Personal Information</h3>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Full Name</label>
              <div className={styles.formInput} style={{ background: '#f8fafc', cursor: 'not-allowed' }}>
                {user?.name || studentName}
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Age</label>
              <div className={styles.formInput} style={{ background: '#f8fafc', cursor: 'not-allowed' }}>
                {(() => {
                  const age = calculateAge((user as any)?.date_of_birth);
                  return age !== null ? `${age} years` : 'Not set';
                })()}
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Current Class</label>
              <div className={styles.formInput} style={{ background: '#f8fafc', cursor: 'not-allowed' }}>
                {user && (user as any).form ? getClassDisplayName() : 'Not set'}
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Phone Number</label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="Enter your phone number"
                className={styles.formInput}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Student Type (Very Important)</label>
              {(boardingLocked || boardingAdminOverride) ? (
                <div style={{ padding: '12px 16px', background: '#fef3c7', borderRadius: 8, border: '1px solid #fde68a', color: '#92400e', fontSize: 14, marginTop: 4 }}>
                  Your student type has been set by your school administrator and cannot be changed.
                  {studentType && (
                    <span style={{ fontWeight: 700, marginLeft: 8 }}>
                      Current: {studentType === 'day_scholar' ? 'Day Scholar' : 'Boarder'}
                    </span>
                  )}
                </div>
              ) : (
                <>
                  <div className={styles.formHint}>
                    This determines your school fees for the current term. You must select the correct option.
                  </div>
                  {loadingStudentType ? (
                    <div className={styles.formInput} style={{ background: '#f8fafc' }}>
                      Loading student type...
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="student-type"
                          value="day_scholar"
                          checked={studentType === 'day_scholar'}
                          onChange={() => setStudentType('day_scholar')}
                        />
                        <span>Day Scholar</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="student-type"
                          value="boarder"
                          checked={studentType === 'boarder'}
                          onChange={() => setStudentType('boarder')}
                        />
                        <span>Boarder</span>
                      </label>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Email Address</label>
              <input
                type="email"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                placeholder="your.email@example.com"
                className={styles.formInput}
              />
              <div className={styles.formHint}>Used for announcements and password recovery</div>
            </div>

            <button
              type="button"
              onClick={handleSaveProfile}
              disabled={savingProfile}
              className={styles.primaryButton}
              style={{ marginTop: 8 }}
            >
              {savingProfile ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderMain = () => {
    switch (activeSection) {
      case 'assignments':
        return <AssignmentsContent />;
      case 'grades':
        return <GradesContent />;
      case 'timetable':
        return <TimetableContent />;
      case 'announcements':
        return <AnnouncementsContent />;
      case 'fees':
        return <StudentFees />;
      case 'profile':
        return <ProfileContent />;
      case 'dashboard':
      default:
        return <DashboardContent />;
    }
  };

  const navItems: { key: Section; label: string; icon: string }[] = [
    { key: 'dashboard', label: 'Dashboard', icon: '📊' },
    { key: 'assignments', label: 'Assignments', icon: '📝' },
    { key: 'grades', label: 'Grades', icon: '📈' },
    { key: 'timetable', label: 'Timetable', icon: '📅' },
    { key: 'announcements', label: 'Announcements', icon: '📢' },
    { key: 'fees', label: 'School Fees', icon: '💰' },
  ];

  return (
    <div className={styles.dashboardContainer} data-school={currentSchool === 'TLC' ? 'tlc' : 'kba'}>
      {/* Mobile Menu Toggle */}
      {isMobileView && (
        <button 
          className={styles.mobileMenuToggle}
          onClick={toggleMobileMenu}
          aria-label="Toggle navigation menu"
          aria-expanded={isMobileMenuOpen}
        >
          ☰
        </button>
      )}

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className={`${styles.mobileOverlay} ${isMobileMenuOpen ? styles.show : ''}`}
          onClick={closeMobileMenu}
          aria-label="Close menu"
        />
      )}

      {isSwitchingSchool && <LoadingOverlay label="Switching school..." />}

      {/* Sidebar */}
      <aside className={`${styles.sidebar} ${isMobileMenuOpen ? styles.mobileOpen : ''}`}>
        <div className={styles.sidebarHeader}>
          <div className={styles.sidebarLogo}>
            <div className={styles.logoIcon}>
              <img 
                src={currentSchool === 'KBA' ? '/kba logo.PNG' : '/tlc logo.jpg'} 
                alt={`${currentSchool} Logo`} 
                style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
              />
            </div>
            <div className={styles.logoText}>
              <span className={styles.sidebarTitle}>{schoolInfo.shortName}</span>
              <span className={styles.sidebarSubtitle}>Student Portal</span>
            </div>
          </div>
        </div>

        <nav className={styles.navSection}>
          <div className={styles.navLabel}>Menu</div>
          {navItems.map(({ key, label, icon }) => (
            <button
              key={key}
              className={`${styles.navButton} ${activeSection === key ? styles.active : ''}`}
              onClick={() => {
                setActiveSection(key);
                if (key === 'assignments') setAssignmentsView('main');
                closeMobileMenu();
              }}
            >
              <span className={styles.navIcon}>{icon}</span>
              {label}
            </button>
          ))}
        </nav>

        <div className={styles.sidebarProfile}>
          <button
            className={styles.profileButton}
            onClick={() => {
              setActiveSection('profile');
              closeMobileMenu();
            }}
          >
            <div className={styles.profileAvatar}>
              {avatarPreview ? (
                <img src={avatarPreview} alt="Profile" />
              ) : (
                studentName.charAt(0).toUpperCase()
              )}
            </div>
            <div className={styles.profileInfo}>
              <div className={styles.profileName}>{user?.name || studentName}</div>
              <div className={styles.profileRole}>
                {(user as any)?.form ? getClassDisplayName() : 'Student'}
              </div>
            </div>
          </button>
          <button 
            className={styles.logoutButton} 
            onClick={() => {
              closeMobileMenu();
              setShowLogoutConfirmation(true);
            }}
          >
            🚪 Sign Out
          </button>
          <div className={styles.sidebarAttribution}>POWERED BY BIZNARY TECH SOLUTIONS</div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={styles.mainContent}>
        {/* Top Bar */}
        <header className={styles.topBar}>
          <div className={styles.topBarLeft}>
            <div className={styles.breadcrumb}>
              Home / <span>{activeSection.charAt(0).toUpperCase() + activeSection.slice(1)}</span>
            </div>
          </div>
          <div className={styles.topBarRight}>
            <button 
              className={styles.topBarButton}
              onClick={() => {
                setActiveSection('announcements');
                setAnnouncementTab('general');
                loadAnnouncements('general');
              }}
              title="Announcements"
            >
              📢
              {newAnnouncementCount > 0 && (
                <span className={styles.topBarNotificationBadge}>{bellBadgeCount}</span>
              )}
            </button>
            <button 
              className={styles.topBarButton}
              onClick={() => setActiveSection('profile')}
              title="Profile"
            >
              👤
            </button>
          </div>
        </header>

        {/* Content Area */}
        <div className={styles.contentWrapper}>
          {renderMain()}
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      <LogoutConfirmationModal
        isOpen={showLogoutConfirmation}
        onConfirm={() => {
          setShowLogoutConfirmation(false);
          logout();
        }}
        onCancel={() => setShowLogoutConfirmation(false)}
      />
    </div>
  );
};
